import { useSelector } from 'react-redux';
import { Feature, hasPermission, hasAnyPermission, hasAllPermissions, isViewerRole, getPermissions } from '../config/permissions';

/**
 * Hook to check permissions for the current user's role.
 * 
 * Usage:
 *   const { can, canAny, canAll, isViewer } = usePermissions();
 *   
 *   if (can(FEATURES.ACTION_APPROVE_REQUEST)) { ... }
 *   if (canAny([FEATURES.ACTION_APPROVE_REQUEST, FEATURES.ACTION_REJECT_REQUEST])) { ... }
 */
export const usePermissions = () => {
  const userData = useSelector((state: any) => state.formConfig.user);
  const role = userData?.role;

  return {
    /** Check if current user has a specific permission */
    can: (feature: Feature): boolean => hasPermission(role, feature),

    /** Check if current user has ANY of the given permissions */
    canAny: (features: Feature[]): boolean => hasAnyPermission(role, features),

    /** Check if current user has ALL of the given permissions */
    canAll: (features: Feature[]): boolean => hasAllPermissions(role, features),

    /** Check if current user is a viewer (read-only) role */
    isViewer: isViewerRole(role),

    /** Get all permissions for the current role */
    permissions: getPermissions(role),

    /** Current role string */
    role,
  };
};
